import { useExperimentBucket } from '@carvana/experiment';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import type React from 'react';
import type { FacetData, FlattenedFacetData } from '~/contracts';
import accumulateFacets from './accumulateFacets';

function isFacetData(v: FlattenedFacetData): v is FacetData {
  const keys: Array<keyof FacetData> = ['makes', 'waysToSave'];

  return keys.some(key => Object.hasOwn(v, key));
}

type FacetDataContextType = {
  baseline: Partial<FacetData>;
  facetData: Partial<FacetData>;
  setFacetData: (facetData: Partial<FacetData>) => void;
};

const FacetDataContext = createContext<FacetDataContextType>({
  baseline: {},
  facetData: {},
  setFacetData() {}
});

function getFacetDataWithBaseline(
  experimentBucket: string,
  baseline: Partial<FacetData>,
  facetData: Partial<FacetData>
): Partial<FacetData> {
  if (experimentBucket === 'show-zero-counts') {
    const facetDataWithBaseline = Object.entries(baseline).reduce(
      accumulateFacets(facetData),
      {}
    );

    return isFacetData(facetDataWithBaseline) ? facetDataWithBaseline : {};
  }

  return baseline;
}

export type OwnProps = {
  baseline?: Partial<FacetData>;
  facetData?: Partial<FacetData>;
};

export function FacetDataContextProvider(
  props: React.PropsWithChildren<OwnProps>
) {
  const experimentBucket = useExperimentBucket('srch-set-reduction2');

  const [baseline] = useState<Partial<FacetData>>(props.baseline ?? {});
  const [facetData, setFacetData] = useState<Partial<FacetData>>(
    getFacetDataWithBaseline(
      experimentBucket,
      props.baseline ?? {},
      props.facetData ?? {}
    )
  );

  useEffect(() => {
    setFacetData(
      getFacetDataWithBaseline(
        experimentBucket,
        props.baseline ?? {},
        props.facetData ?? {}
      )
    );
  }, [experimentBucket, props.baseline, props.facetData]);

  const setFacetDataExperiment = useCallback(
    (newFacetData: Partial<FacetData>) => {
      setFacetData(
        getFacetDataWithBaseline(experimentBucket, baseline, newFacetData)
      );
    },
    [baseline, experimentBucket]
  );

  const value = useMemo<FacetDataContextType>(
    () => ({
      baseline,
      facetData,
      setFacetData: setFacetDataExperiment
    }),
    [baseline, facetData, setFacetDataExperiment]
  );

  return (
    <FacetDataContext.Provider value={value}>
      {props.children}
    </FacetDataContext.Provider>
  );
}

FacetDataContextProvider.displayName = 'FacetDataContextProvider';

export default FacetDataContext;
